import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  Icon as PhosphorIcon,
  IconProps as PhosphorIconProps,
} from "@phosphor-icons/react";
import { cn } from "../../lib/utils";

const iconVariants = cva("ui-flex ui-items-center ui-justify-center", {
  variants: {
    size: {
      xxxxs: 10,
      xxxs: 12,
      xxs: 14,
      xs: 16,
      sm: 18,
      md: 22,
      lg: 28,
      xl: 29.25,
    },
    wrapper: {
      sm: "ui-h-5 ui-w-5",
      md: "ui-h-6 ui-w-6",
      lg: "ui-h-8 ui-w-8",
      xl: "ui-h-10 ui-w-10",
    },
  },
  defaultVariants: {
    size: "xxxs",
  },
});

export interface IconProps
  extends VariantProps<typeof iconVariants>,
    React.HTMLAttributes<HTMLSpanElement> {
  IconComponent: PhosphorIcon;
  iconProps?: Omit<PhosphorIconProps, "size">;
  size: NonNullable<VariantProps<typeof iconVariants>["size"]>;
}

const VARIANT_SIZE_MAP = {
  xxxxs: 10,
  xxxs: 12,
  xxs: 14,
  xs: 16,
  sm: 18,
  md: 22,
  lg: 28,
  xl: 36,
};

const Icon = ({ size, IconComponent, iconProps, ...props }: IconProps) => {
  const _IconComponent = (
    <IconComponent
      {...iconProps}
      size={VARIANT_SIZE_MAP[size]}
      className={cn(iconProps?.className, props.className)}
    />
  );
  if (size === "xxxxs" || size === "xxxs" || size === "xxs" || size === "xs") {
    return _IconComponent;
  }

  return (
    <span {...props} className={cn(iconVariants({ wrapper: size }), props.className)} >
      {_IconComponent}
    </span>
  );
};

Icon.displayName = "Icon";

export { Icon, iconVariants };

export type { PhosphorIcon };
